import { useState, useEffect, useRef, FormEvent } from 'react';
import { ChatConversation, useChat } from '../../../../contexts/ChatContext';

interface ConversationViewProps {
  conversation: ChatConversation;
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function formatDateLabel(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long' });
}

function groupByDate(messages: ChatConversation['messages']) {
  const groups: { date: string; messages: typeof messages }[] = [];
  messages.forEach((msg) => {
    const label = formatDateLabel(msg.timestamp);
    const last = groups[groups.length - 1];
    if (last && last.date === label) {
      last.messages.push(msg);
    } else {
      groups.push({ date: label, messages: [msg] });
    }
  });
  return groups;
}

export default function ConversationView({ conversation }: ConversationViewProps) {
  const { sendAdminReply, markConversationRead, allConversations } = useChat();
  const [reply, setReply] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get the latest version of this conversation
  const liveConv = allConversations.find((c) => c.id === conversation.id) ?? conversation;
  const groups = groupByDate(liveConv.messages);

  useEffect(() => {
    markConversationRead(conversation.id);
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.id, markConversationRead]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [liveConv.messages.length]);

  const handleSend = (e: FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) return;
    sendAdminReply(conversation.id, reply.trim());
    setReply('');
    inputRef.current?.focus();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-stone-100 flex items-center gap-3 shrink-0">
        <div className="w-9 h-9 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shrink-0">
          <span className="text-white text-sm font-bold">
            {liveConv.userName.charAt(0).toUpperCase()}
          </span>
        </div>
        <div>
          <p className="text-sm font-semibold text-stone-900">{liveConv.userName}</p>
          <p className="text-xs text-stone-400">{liveConv.messages.length} messages</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {groups.map((group) => (
          <div key={group.date}>
            {/* Date separator */}
            <div className="flex items-center gap-3 my-3">
              <div className="flex-1 h-px bg-stone-100" />
              <span className="text-[10px] text-stone-400 font-medium px-2">{group.date}</span>
              <div className="flex-1 h-px bg-stone-100" />
            </div>

            <div className="space-y-2">
              {group.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.sender === 'user' && (
                    <div className="w-7 h-7 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mr-2 mt-0.5 shrink-0">
                      <span className="text-white text-xs font-bold">
                        {liveConv.userName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div
                    className={`max-w-[70%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.sender === 'admin'
                        ? 'bg-orange-500 text-white rounded-br-sm'
                        : 'bg-stone-100 text-stone-800 rounded-bl-sm'
                    }`}
                  >
                    <p>{msg.text}</p>
                    <p
                      className={`text-[10px] mt-0.5 ${
                        msg.sender === 'admin' ? 'text-orange-200' : 'text-stone-400'
                      }`}
                    >
                      {formatTime(msg.timestamp)}
                    </p>
                  </div>
                  {msg.sender === 'admin' && (
                    <div className="w-7 h-7 bg-orange-100 rounded-full flex items-center justify-center ml-2 mt-0.5 shrink-0">
                      <i className="ri-user-fill text-orange-500 text-xs" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Reply input */}
      <form
        onSubmit={handleSend}
        className="px-4 py-3 border-t border-stone-100 flex items-center gap-2 shrink-0"
      >
        <input
          ref={inputRef}
          type="text"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder={`Reply to ${liveConv.userName}...`}
          className="flex-1 px-4 py-2.5 text-sm bg-stone-100 rounded-xl outline-none focus:bg-stone-200 transition"
        />
        <button
          type="submit"
          disabled={!reply.trim()}
          className="w-10 h-10 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white rounded-xl flex items-center justify-center transition cursor-pointer shrink-0"
        >
          <i className="ri-send-plane-fill text-sm" />
        </button>
      </form>
    </div>
  );
}
